/**
 * Groups dated chart points into buckets wide enough to stay readable.
 *
 * A bar per day is fine for a couple of weeks and unreadable for a year, so the span picks
 * the bucket: days, then weeks, then months. That keeps the group count bounded no matter
 * how wide a range the reader asks for, which a zoom control cannot do — at 365 daily
 * groups every bar is already thinner than the gap beside it.
 *
 * Keys are local calendar dates (`YYYY-MM-DD`), not UTC: a reader in UTC+3 expects an
 * inspection saved at 01:00 on the 5th to land on the 5th, and `toISOString()` would file
 * it under the 4th. They sort lexicographically, so a sorted key is a sorted date.
 */

export type Granularity = 'day' | 'week' | 'month';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_LENGTH_DAYS = 7;

/** Past these spans a bar per day (or per week) is too thin to read. */
const MAX_DAILY_SPAN_DAYS = 31;
const MAX_WEEKLY_SPAN_DAYS = 182;

export const granularityForSpan = (dates: Date[]): Granularity => {
  if (dates.length === 0) return 'day';

  let earliest = dates[0].getTime();
  let latest = earliest;

  for (const date of dates) {
    const time = date.getTime();
    if (time < earliest) earliest = time;
    if (time > latest) latest = time;
  }

  const spanDays = Math.round((latest - earliest) / DAY_MS) + 1;

  if (spanDays <= MAX_DAILY_SPAN_DAYS) return 'day';
  if (spanDays <= MAX_WEEKLY_SPAN_DAYS) return 'week';

  return 'month';
};

/** The first day of the bucket the date falls in. Weeks start Monday. */
export const bucketStart = (date: Date, granularity: Granularity): Date => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);

  if (granularity === 'week') {
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  } else if (granularity === 'month') {
    start.setDate(1);
  }

  return start;
};

export const bucketKey = (date: Date, granularity: Granularity): string => {
  const start = bucketStart(date, granularity);
  const month = String(start.getMonth() + 1).padStart(2, '0');
  const day = String(start.getDate()).padStart(2, '0');

  return `${start.getFullYear()}-${month}-${day}`;
};

export const formatBucket = (key: string, granularity: Granularity, locale: string): string => {
  const [year, month, day] = key.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  if (granularity === 'month') {
    return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' });
  }

  // A week named after its first day alone reads as that single day, and nothing else on the
  // chart says the granularity changed — so the label names the span the bar actually counts.
  // `formatRange` collapses the repeated month ("11 – 17 May") and adds years only when the
  // week crosses one, which keeps the tick as narrow as the ambiguous form was.
  if (granularity === 'week') {
    const end = new Date(year, month - 1, day + WEEK_LENGTH_DAYS - 1);

    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).formatRange(date, end);
  }

  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
};
